import type { ICollectableCoupon } from '@/interfaces/collectableCoupon';

/**
 * Min viewport height needed for a 2-row grid to fit inside the modal
 * body's `max-h-[75vh]` clip. Per `effectiveCols` (not pageSize) so the
 * threshold matches the actual columns being rendered — important for
 * N=4 on a tablet where the grid is 2 cols but pageSize is 3.
 *   cols=2 → h≥580
 *   cols=3 → h≥700
 *   cols=4 → h≥680
 * Mobile (cols=1) is Infinity (rows locked to 1).
 */
export const VH_MIN_FOR_2_ROWS_BY_COLS: Record<number, number> = {
  2: 580,
  3: 700,
  4: 680,
};

export const vhMinFor2Rows = (cols: number): number => {
  if (cols === 1) return Infinity;
  return VH_MIN_FOR_2_ROWS_BY_COLS[cols] ?? 700;
};

export interface GridResult {
  cols: number;
  rows: 1 | 2;
  slidesPerPage: number;
  totalPages: number;
}

/**
 * Grid rule for the floating collectable-coupon modal:
 *   - Mobile (pageSize=1): single-card, locked to 1 col x 1 row.
 *   - Tablet / desktop: count-driven cols + viewport-fit rows.
 *
 *   N=1,2,3 → 1 row, cols = N (single row, no trailing orphan).
 *   N=4    → 4 cols x 1 row if viewport wide enough (≥1280px),
 *              else 2 cols x 2 rows when 2 rows fit; else cols=2 rows=1.
 *   N>=5   → cols = viewport max, rows = 2 when viewport tall enough.
 *
 * Rows are capped at 2. Mobile (cols=1) is locked to rows=1.
 */
export const buildGrid = (
  n: number,
  pageSize: number,
  viewportHeight: number,
): GridResult => {
  let cols: number;
  let rows: 1 | 2;

  if (pageSize === 1) {
    // Mobile: single card, fixed grid.
    cols = 1;
    rows = 1;
  } else if (n <= 3) {
    // Few cards: pack into a single row so we never get a trailing
    // orphan. Columns capped by viewport max.
    cols = Math.min(Math.max(n, 1), pageSize);
    rows = 1;
  } else if (n === 4) {
    // N=4: prefer 4 cols × 1 row when the viewport is wide enough
    // (≥1280px so 4×240+3×19.2=1017.6px fits). Otherwise fall back
    // to 2 cols × 2 rows ("2x2") when 2 rows fit; single row of 2
    // as a tight squeeze fallback.
    if (pageSize >= 4) {
      cols = 4;
      rows = 1;
    } else {
      cols = 2;
      rows = viewportHeight >= vhMinFor2Rows(2) ? 2 : 1;
    }
  } else {
    // Mid (5-6) and larger (>=7) counts: fill cols to viewport max,
    // 2 rows when the viewport is tall enough for a 2-row grid.
    cols = pageSize;
    rows = viewportHeight >= vhMinFor2Rows(pageSize) ? 2 : 1;
  }

  const slidesPerPage = cols * rows;
  const totalPages = Math.max(1, Math.ceil(n / slidesPerPage));

  return { cols, rows, slidesPerPage, totalPages };
};

/**
 * Slice a coupon list into per-page chunks. Each page's chunk is then
 * split into rowChunks (cols-per-row) by the caller for layout.
 */
export const chunkByPage = <T,>(items: T[], slidesPerPage: number): T[][] => {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += slidesPerPage) {
    pages.push(items.slice(i, i + slidesPerPage));
  }
  if (pages.length === 0) pages.push([]);
  return pages;
};

/**
 * Split a single page's coupons into rows of `cols` for layout. A
 * partial last row (fewer than `cols` items) is preserved as-is.
 */
export const chunkByRow = <T,>(page: T[], cols: number): T[][] => {
  const rows: T[][] = [];
  for (let i = 0; i < page.length; i += cols) {
    rows.push(page.slice(i, i + cols));
  }
  return rows;
};

export type { ICollectableCoupon };